import { useEffect, useRef } from 'react'
import { t, anzahlwort } from './sprache'
import type { Fund } from './Suche'

/**
 * Die Suche in der Berichtsleiste.
 *
 * **Ein Symbol, das aufklappt** — kein Feld, das dauerhaft Platz belegt. Die
 * Suche ist häufig genug für eine eigene Schaltfläche und selten genug, um
 * neben Spaltenwahl und Anzeige-Zusätzen nicht mitzuzählen. Strg+F öffnet sie
 * ebenso, weil jeder Anwender diesen Griff mitbringt.
 *
 * **Der Treffer wird angesprungen, nicht gefiltert.** Eine Bilanz, die auf
 * ihre Treffer zusammenschrumpft, zeigt Summen, die nicht mehr zu den
 * sichtbaren Zeilen passen — ein bekannter Anlass für Fehlschlüsse. Stattdessen
 * bleibt die Tabelle stehen, die Trefferzeile wird hervorgehoben, und der
 * Zähler sagt, wo man ist.
 */
export function Suchfeld({
  offen,
  eingabe,
  funde,
  index,
  aufOeffnen,
  aufEingabe,
  aufSprung,
}: {
  offen: boolean
  eingabe: string
  funde: Fund[]
  index: number
  aufOeffnen: (offen: boolean) => void
  aufEingabe: (text: string) => void
  aufSprung: (schritt: number) => void
}) {
  const feld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (offen) {
      feld.current?.focus()
      feld.current?.select()
    }
  }, [offen])

  if (!offen) {
    return (
      <button
        type="button"
        className="suchknopf"
        aria-label={t('suche.oeffnen')}
        title={t('suche.oeffnen')}
        onClick={() => aufOeffnen(true)}
      >
        <Lupe />
      </button>
    )
  }

  const texte = funde.filter((f) => f.art === 'text').length
  const werte = funde.length - texte
  const aktiv = funde[index]

  return (
    <div className="suchfeld">
      <div className="suchzeile">
        <span className="suchsymbol" aria-hidden="true">
          <Lupe />
        </span>

        <input
          ref={feld}
          type="search"
          value={eingabe}
          aria-label={t('suche.titel')}
          placeholder={t('suche.platzhalter')}
          onChange={(e) => aufEingabe(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              aufSprung(e.shiftKey ? -1 : 1)
            }

            if (e.key === 'Escape') {
              e.preventDefault()
              aufOeffnen(false)
            }
          }}
        />

        <span className="suchzaehler" role="status">
          {eingabe.trim().length === 0
            ? ''
            : funde.length === 0
              ? t('suche.leer')
              : t('suche.zaehler', { nummer: String(index + 1), anzahl: anzahlwort(funde.length) })}
        </span>

        <button
          type="button"
          aria-label={t('suche.zurueck')}
          title={t('suche.zurueck')}
          disabled={funde.length === 0}
          onClick={() => aufSprung(-1)}
        >
          ▴
        </button>
        <button
          type="button"
          aria-label={t('suche.weiter')}
          title={t('suche.weiter')}
          disabled={funde.length === 0}
          onClick={() => aufSprung(1)}
        >
          ▾
        </button>
        <button
          type="button"
          aria-label={t('suche.schliessen')}
          title={t('suche.schliessen')}
          onClick={() => aufOeffnen(false)}
        >
          ✕
        </button>
      </div>

      {/*
        Die Statuszeile ist der eigentliche Ertrag: Sie nennt die Zeile, in der
        der Treffer steht, samt ihrem Weg dorthin — „B.I. Vorräte → 1200 ·
        Vorräte → NL-PROD". Und sie sagt in Worten, was ein Farbton nicht sagen
        könnte: dass der Betrag nur mit umgekehrtem Vorzeichen passt (R143).
      */}
      {aktiv && (
        <p className="suchstand erlaeuterung">
          <span className="fundname">{aktiv.name}</span>
          {aktiv.umgekehrt && <span className="fundmarker">{t('suche.umgekehrt')}</span>}
          {texte > 0 && werte > 0 && (
            <span className="fundgruppen">{t('suche.gruppen', { texte: anzahlwort(texte), werte: anzahlwort(werte) })}</span>
          )}
        </p>
      )}
    </div>
  )
}

/** Die Lupe — ein Umriss, kein Bild: Sie erbt die Schriftfarbe ihrer Umgebung. */
function Lupe() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10.4 10.4 L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
